// src/pages/ForgotPassword.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Car, Navigation, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from '../components/ui/ThemeToggle';
import { auth as authApi } from '../services/api';

export default function ForgotPassword() {
  const theme = useTheme();

  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [loaded,  setLoaded]  = useState(false);

  useEffect(() => { const t = setTimeout(() => setLoaded(true), 80); return () => clearTimeout(t); }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    await authApi.forgot(email.trim());
    setLoading(false);
    setSent(true);
  }

  const inputStyle = { backgroundColor: theme.background, borderColor: theme.border, color: theme.text };

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ backgroundColor: theme.background }}>

      {/* Blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-[600px] h-[600px] rounded-full blur-3xl opacity-20 -top-32 -left-32"
          style={{ background: 'radial-gradient(circle, #00BCD4, transparent)' }} />
        <div className="absolute w-[500px] h-[500px] rounded-full blur-3xl opacity-15 -bottom-20 -right-20"
          style={{ background: 'radial-gradient(circle, #4CAF50, transparent)' }} />
      </div>

      {/* Centered layout */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-10">

        {/* Card */}
        <div className="w-full max-w-[400px] rounded-3xl p-8 transition-all duration-700"
          style={{
            backgroundColor: theme.card,
            backdropFilter: 'blur(24px)',
            border: `1px solid ${theme.border}`,
            boxShadow: theme.isDark ? '0 24px 80px rgba(0,0,0,0.5)' : '0 24px 80px rgba(0,0,0,0.1)',
            transform: loaded ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
            opacity: loaded ? 1 : 0,
          }}>

          {/* Logo + toggle row */}
          <div className="flex items-center justify-between mb-7">
            <img src="/logo.png" alt="TripData" className="h-7 object-contain" />
            <ThemeToggle />
          </div>

          {sent ? (
            <>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold mb-2 text-center" style={{ color: theme.text }}>Check your email</h1>
              <p className="text-sm leading-relaxed mb-6 text-center" style={{ color: theme.textMuted }}>
                If <span style={{ color: theme.text }}>{email}</span> is registered, a reset link is on its way.
              </p>
              <Link to="/login" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: '#00ACC1' }}>
                <ArrowRight size={14} className="rotate-180" />Back to sign in
              </Link>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-2xl mb-5 mx-auto flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
                <Navigation size={22} color="white" />
              </div>
              <h1 className="text-2xl font-bold mb-1 text-center" style={{ color: theme.text }}>Reset password</h1>
              <p className="text-sm mb-7 text-center" style={{ color: theme.textMuted }}>
                Enter your email and we'll send a reset link.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: theme.textMuted }}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required autoComplete="email" placeholder="you@example.com"
                    className="w-full px-4 py-3 rounded-2xl text-sm outline-none transition-all border"
                    style={inputStyle} />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3.5 rounded-2xl text-white font-bold text-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)', boxShadow: '0 8px 32px rgba(0,172,193,0.35)' }}>
                  {loading ? 'Sending...' : <><span>Send reset link</span><ArrowRight size={16} /></>}
                </button>
              </form>

              <p className="text-sm mt-6" style={{ color: theme.textMuted }}>
                Remember it?{' '}
                <Link to="/login" className="font-semibold" style={{ color: '#00ACC1' }}>Sign in</Link>
              </p>
            </>
          )}
        </div>

        {/* Back to website */}
        <a href="https://tripdata.co.za"
          className="flex items-center gap-1.5 mt-5 text-xs font-medium transition-opacity hover:opacity-70"
          style={{ color: theme.textMuted }}>
          <ArrowRight size={12} className="rotate-180" />
          Back to tripdata.co.za
        </a>
      </div>

    </div>
  );
}